import { useState, useEffect, useCallback } from "react";
import { ServiceRecord } from "../service-apis/serviceTypes";
import { ServiceApiService } from "../service-apis/serviceManagementService";
import { PermissionService } from "../service-apis/permissionService";
import { API_CONFIG } from "../../../config/netConfig";
import { getHeaders } from "../utils/authUtils";

/**
 * 服务详情相关的数据获取和状态管理 Hook
 */
export const useServiceDetail = (selectedService: ServiceRecord | null) => {
  // 服务详情
  const [serviceDetail, setServiceDetail] = useState<any>(null);
  // 服务token列表
  const [serviceTokens, setServiceTokens] = useState<any[]>([]);
  // 权限数据
  const [permissions, setPermissions] = useState<any[]>([]);
  // 管理员数据
  const [administrators, setAdministrators] = useState<any[]>([]);
  // 管理员组数据
  const [adminGroups, setAdminGroups] = useState<any[]>([]);
  // 详情加载状态
  const [detailLoading, setDetailLoading] = useState(false);

  // 获取服务详情
  const fetchServiceDetail = useCallback(async (serviceNameOrId: string) => {
    if (!serviceNameOrId) return;
    setDetailLoading(true);
    try {
      // 后端API使用服务名称，而不是ID
      const response = await ServiceApiService.getServiceDetail(serviceNameOrId);
      console.log("[获取服务详情] 响应:", response);

      // 处理不同的响应格式
      let detailData: any = null;
      if (response && typeof response === "object") {
        // 标准格式: { success: true, data: {...} }
        if ("success" in response && response.success) {
          detailData = response.data;
        }
        // 直接是数据对象
        else if ("id" in response || "name" in response || "service_name" in response) {
          detailData = response;
        }
        // 数据在data字段中
        else if ("data" in response) {
          detailData = response.data;
        } else {
          console.warn("[获取服务详情] 未知的响应格式:", response);
          setServiceDetail(null);
          return;
        }
      } else {
        console.warn("[获取服务详情] 响应不是对象:", response);
        setServiceDetail(null);
        return;
      }

      // 处理 required_permissions 字段：如果是数组，转换为逗号分隔的字符串
      if (detailData) {
        const requiredPermissions =
          detailData.required_permissions ||
          detailData.requiredPermissions ||
          detailData.permissions;
        if (Array.isArray(requiredPermissions)) {
          // 数组转换为逗号分隔的字符串（格式：xxx:yyy:zzz, aaa:bbb:ccc）
          detailData.required_permissions = requiredPermissions.join(", ");
          detailData.requiredPermissions = detailData.required_permissions;
          detailData.permissions = detailData.required_permissions;
        } else if (typeof requiredPermissions === "string") {
          // 已经是字符串，直接使用
          detailData.required_permissions = requiredPermissions;
          detailData.requiredPermissions = requiredPermissions;
          detailData.permissions = requiredPermissions;
        }
      }

      setServiceDetail(detailData);
    } catch (error: any) {
      // 处理404错误 - 服务不存在
      if (error?.response?.status === 404) {
        console.warn(`[获取服务详情] 服务不存在 (404): serviceNameOrId=${serviceNameOrId}`);
        setServiceDetail(null);
        // 可以选择显示提示信息，但不抛出错误
        return;
      }

      // 其他错误
      console.error("获取服务详情失败:", {
        error,
        message: error?.message || String(error),
        response: error?.response?.data,
        status: error?.response?.status,
        serviceNameOrId,
      });
      setServiceDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // 获取服务token列表
  const fetchServiceTokens = useCallback(async (serviceId: string) => {
    if (!serviceId) return;
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}/services/${serviceId}/tokens`, {
        headers: getHeaders(),
      });
      const data = await response.json();
      if (data.success && data.data?.tokens) {
        setServiceTokens(data.data.tokens || []);
      }
    } catch (error) {
      console.error("获取服务token列表失败:", error);
      setServiceTokens([]);
    }
  }, []);

  // 获取权限数据
  const fetchPermissions = useCallback(async (serviceName: string) => {
    if (!serviceName) return;
    try {
      const response: any = await PermissionService.fetchServicePermissions(serviceName);
      if (Array.isArray(response)) {
        setPermissions(response);
      } else if (response && typeof response === "object" && "success" in response && response.success) {
        setPermissions(response.data || []);
      } else {
        setPermissions([]);
      }
    } catch (error) {
      console.error("获取权限数据失败:", error);
      setPermissions([]);
    }
  }, []);

  // 获取管理员数据（根据serviceId）
  const fetchAdministrators = useCallback(async (serviceId: string) => {
    if (!serviceId) return;
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}/services/${serviceId}/admins`, {
        headers: getHeaders(),
      });
      const data = await response.json();
      console.log("[获取管理员数据] API响应:", { serviceId, response: data });
      if (data.success && data.data) {
        // 处理不同的响应格式
        if (Array.isArray(data.data)) {
          console.log("[获取管理员数据] 设置为数组:", data.data);
          setAdministrators(data.data);
        } else if (data.data.admins && Array.isArray(data.data.admins)) {
          console.log("[获取管理员数据] 设置为data.admins:", data.data.admins);
          setAdministrators(data.data.admins);
        } else if (data.data.items && Array.isArray(data.data.items)) {
          console.log("[获取管理员数据] 设置为data.items:", data.data.items);
          setAdministrators(data.data.items);
        } else {
          console.warn("[获取管理员数据] 未知的数据格式:", data.data);
          setAdministrators([]);
        }
      } else {
        console.warn("[获取管理员数据] API返回失败或没有数据:", data);
        // 如果API没有返回数据，至少设置默认的管理员数据
        setAdministrators([
          { id: "admin", username: "admin", name: "admin", admin_name: "admin" },
          { id: "admins", username: "admins", name: "admins", admin_name: "admins" },
        ]);
      }
    } catch (error) {
      console.error("获取管理员数据失败:", error);
      // 即使API调用失败，也设置默认的管理员数据
      setAdministrators([
        { id: "admin", username: "admin", name: "admin", admin_name: "admin" },
        { id: "admins", username: "admins", name: "admins", admin_name: "admins" },
      ]);
    }
  }, []);

  // 获取管理员组数据（根据serviceId）
  const fetchAdminGroups = useCallback(async (serviceId: string) => {
    if (!serviceId) return;
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}/services/${serviceId}/roles`, {
        headers: getHeaders(),
      });
      const data = await response.json();
      console.log("[获取管理员组数据] API响应:", { serviceId, response: data });
      if (data.success && data.data) {
        // 处理不同的响应格式
        if (Array.isArray(data.data)) {
          console.log("[获取管理员组数据] 设置为数组:", data.data);
          setAdminGroups(data.data);
        } else if (data.data.roles && Array.isArray(data.data.roles)) {
          console.log("[获取管理员组数据] 设置为data.roles:", data.data.roles);
          setAdminGroups(data.data.roles);
        } else if (data.data.items && Array.isArray(data.data.items)) {
          console.log("[获取管理员组数据] 设置为data.items:", data.data.items);
          setAdminGroups(data.data.items);
        } else {
          console.warn("[获取管理员组数据] 未知的数据格式:", data.data);
          setAdminGroups([]);
        }
      } else {
        console.warn("[获取管理员组数据] API返回失败或没有数据:", data);
        // 如果API没有返回数据，至少设置默认的管理员组数据
        setAdminGroups([
          { id: "admins", name: "admins", role_name: "admins", display_name: "admins" },
        ]);
      }
    } catch (error) {
      console.error("获取管理员组数据失败:", error);
      // 即使API调用失败，也设置默认的管理员组数据
      setAdminGroups([
        { id: "admins", name: "admins", role_name: "admins", display_name: "admins" },
      ]);
    }
  }, []);

  // 当选中服务变化时，获取相关数据
  useEffect(() => {
    if (selectedService) {
      // 使用服务名称获取详情（后端API使用名称而不是ID）
      const serviceName = selectedService.name || selectedService.service_name || selectedService.key || "";
      if (serviceName) {
        fetchServiceDetail(serviceName);
      }
      // Token API使用ID
      if (selectedService.id) {
        fetchServiceTokens(selectedService.id);
        // 管理员和管理员组API使用serviceId
        fetchAdministrators(selectedService.id);
        fetchAdminGroups(selectedService.id);
      }
      fetchPermissions(serviceName);
    } else {
      setServiceDetail(null);
      setServiceTokens([]);
      setPermissions([]);
      setAdministrators([]);
      setAdminGroups([]);
    }
  }, [selectedService, fetchServiceDetail, fetchServiceTokens, fetchPermissions, fetchAdministrators, fetchAdminGroups]);

  return {
    serviceDetail,
    serviceTokens,
    permissions,
    administrators,
    adminGroups,
    detailLoading,
    // 提供刷新方法
    refreshServiceDetail: () => {
      if (selectedService) {
        const serviceName = selectedService.name || selectedService.service_name || selectedService.key || "";
        if (serviceName) fetchServiceDetail(serviceName);
      }
    },
    refreshServiceTokens: () => {
      if (selectedService?.id) {
        fetchServiceTokens(selectedService.id);
      }
    },
    refreshPermissions: () => {
      if (selectedService) {
        const serviceName = selectedService.name || selectedService.service_name || selectedService.key || "";
        if (serviceName) fetchPermissions(serviceName);
      }
    },
    refreshAdministrators: () => {
      if (selectedService?.id) {
        fetchAdministrators(selectedService.id);
      }
    },
    refreshAdminGroups: () => {
      if (selectedService?.id) {
        fetchAdminGroups(selectedService.id);
      }
    },
  };
};

